import { lazy } from 'react';
import { APP_NAME } from '@config';
const LoginPage = lazy(() => import('@plugin/auth/views/LoginPageView'));

const Dashboard = lazy(() => import('@plugin/dashboard/views/dashboardView'));
const CustomerListView = lazy(() => import('@plugin/customer/views/CustomerListView'));
const LeadListView = lazy(() => import('@plugin/leads/views/LeadListView'));
const TaskListView = lazy(() => import('@plugin/task/views/TaskListView'));
const ProductsView = lazy(() => import('@plugin/products/views/ProductsView'));
const WorkLogListView = lazy(() => import('@plugin/worklog/views/WorkLogListView'));
const CategoriesListView = lazy(() => import('@plugin/categories/views/CategoriesListView'));
const ModulesListView = lazy(() => import('@plugin/modules/views/ModulesListView'));
const UsersListView = lazy(() => import('@plugin/users/views/UsersListView'));
const ReceiptsListView = lazy(() => import('@plugin/Finance/Receipts/views/ReceiptsListView'));
const UserProfileSettings = lazy(() => import('@plugin/myProfile/views/UserProfileSettings'));
const CreateOrg = lazy(() => import('@plugin/organizations/views/CreateOrg'));
const IntegrationsHub = lazy(() => import('@plugin/integrations-hub/views/IntegrationsHub'));
const EmailIntegrations = lazy(() => import('@ws-utils/ws-integrations/email-providers/EmailIntegrations'));

export const coreRoutes = [
  {
    path: "/login",
    element: <LoginPage />,
    seo: {
      title: `Login | ${APP_NAME}`,
      description:
        `Securely log in to your ${APP_NAME} account to manage inventory, contracts, invoices, and returns.`,
      keywords: "webtrix24 login, business crm login, computer business login"
    }
  },
  {
    path: "/dashboard",
    element: <Dashboard />,
    seo: {
      title: `Dashboard | ${APP_NAME}`,
      description:
        `View KPIs, tasks, pipeline, and operational insights from your ${APP_NAME} dashboard.`
    }
  },
  {
    path: "/task",
    element: <TaskListView />,
    seo: {
      title: `Tasks | ${APP_NAME}`,
      description:
        `Plan, assign, and track tasks across your business operations to keep your team on schedule.`
    }
  },
  {
    path: "/customer",
    element: <CustomerListView />,
    seo: {
      title: `Customers | ${APP_NAME}`,
      description:
        `Manage customer profiles, communication, and business history in one place.`
    }
  },
  {
    path: "/leads",
    element: <LeadListView />,
    seo: {
      title: `Leads | ${APP_NAME}`,
      description:
        `Capture, qualify, and convert leads into customers with a streamlined business sales process.`
    }
  },
  {
    path: "/products",
    element: <ProductsView />,
    seo: {
      title: `Products & Inventory | ${APP_NAME}`,
      description:
        `Track laptops, desktops, and assets with serials, warranty, and availability in your inventory.`
    }
  },
  {
    path: "/products/:product_id",
    element: <ProductsView />,
    skipPermission: true,
    seo: {
      title: `Product Details | ${APP_NAME}`,
      description:
        `View product specifications, stock status, Product history, and service details.`
    }
  },
  {
    path: "/workReport",
    element: <WorkLogListView />,
    seo: {
      title: `Work Logs & Reports | ${APP_NAME}`,
      description:
        `Log work, compare estimates vs. actuals, and review productivity across your team.`
    }
  },
  {
    path: "/receipts",
    element: <ReceiptsListView />,
    seo: {
      title: `Receipts | ${APP_NAME}`,
      description:
        `Record payments, reconcile receipts, and maintain a clear audit trail for every transaction.`
    }
  },
  {
    path: "/category",
    element: <CategoriesListView />,
    seo: {
      title: `Categories | ${APP_NAME}`,
      description:
        `Organize products and records with categories for faster filtering and reporting.`
    }
  },
  {
    path: "/modules",
    element: <ModulesListView />,
    seo: {
      title: `Modules | ${APP_NAME}`,
      description:
        `Configure modules and features to match your business workflows and teams.`
    }
  },
  {
    path: "/users",
    element: <UsersListView />,
    seo: {
      title: `Users & Permissions | ${APP_NAME}`,
      description:
        `Add users, assign roles, and control access with fine-grained permissions.`
    }
  },
  {
    path: "/my/settings",
    skipPermission: true,
    element: <UserProfileSettings />,
    seo: {
      title: `My Profile & Settings | ${APP_NAME}`,
      description:
        `Update your profile, preferences, and notification settings.`
    }
  },
  {
    path: "/app/settings",
    element: <CreateOrg />,
    seo: {
      title: `Organization Settings | ${APP_NAME}`,
      description:
        `Manage organization details, branches, taxes, invoice templates, and branding.`
    }
  },
  {
    path: "/integrations",
    element: <IntegrationsHub />,
    seo: {
      title: `Integrations | ${APP_NAME}`,
      description:
        `Connect WhatsApp, email, payment gateways, and more to streamline operations.`
    }
  },
  {
    path: "/integrations/email-provider",
    element: <EmailIntegrations />,
    seo: {
      title: `Integrations | Email Providers | ${APP_NAME}`,
      description:
        `Connect with Varius email provider and start sending emails.`
    }
  }
];
